import { HttpClient, HttpParams } from "@angular/common/http";
import { Injectable, inject } from "@angular/core";
import { environment } from "@env/environment";
import { TenantUrlUtilService } from "@shared/services/tenant-url-util.service";
import { Observable, catchError, throwError } from "rxjs";
import { Voucher } from "../../order-actions/models/voucher.model";
import {
  Invoice,
  InvoicingDocumentResponse,
  ResendEmailPayload,
  TransactionInvoiceResponse,
} from "../models/invoice.models";
interface VoucherPDFResponse {
  code: number;
  message: string;
  data: {
    url: string;
  };
}

@Injectable({
  providedIn: "root",
})
export class InvoiceService {
  private readonly http = inject(HttpClient);
  private readonly tenantUrlUtil = inject(TenantUrlUtilService);

  private readonly TRANSACTION_API_BASE = `${environment.API_TRANSACTION}/api/customer-care/v1`;
  private readonly INVOICING_API_BASE = environment.API_INVOICING;

  getTransactionInvoices(
    tenantId: string,
    identityDocument: string,
    dateFrom: string,
    dateTo: string,
  ): Observable<TransactionInvoiceResponse> {
    const url = `${
      this.TRANSACTION_API_BASE
    }/${tenantId.toLowerCase()}/invoices/${identityDocument}`;

    const httpParams = new HttpParams()
      .set("date-from", dateFrom)
      .set("date-to", dateTo);

    return this.http.get<TransactionInvoiceResponse>(url, {
      params: httpParams,
    });
  }

  getTransactionInvoicesCurrentTenant(
    identityDocument: string,
    dateFrom: string,
    dateTo: string,
  ): Observable<TransactionInvoiceResponse> {
    const tenantId = this.tenantUrlUtil.getTenantId();
    if (!tenantId) {
      throw new Error("No tenant ID found in current route");
    }

    return this.getTransactionInvoices(
      tenantId,
      identityDocument,
      dateFrom,
      dateTo,
    );
  }

  getInvoicingDocuments(params: {
    tenantId: string;
    identityDocument?: string;
    email?: string;
    orderId?: string;
    dateRange?: { dateFrom: string; dateTo: string } | null;
  }): Observable<InvoicingDocumentResponse> {
    const url = `${this.INVOICING_API_BASE}/${params.tenantId}/documents`;

    let httpParams = new HttpParams();

    if (params.identityDocument) {
      httpParams = httpParams.set("identityDocument", params.identityDocument);
    } else if (params.email) {
      httpParams = httpParams.set("email", params.email);
    } else if (params.orderId) {
      httpParams = httpParams.set("order-id", params.orderId);
    } else {
      throw new Error("At least one search parameter required");
    }

    if (params.dateRange) {
      httpParams = httpParams
        .set("dateFrom", params.dateRange.dateFrom)
        .set("dateTo", params.dateRange.dateTo);
    }

    return this.http.get<InvoicingDocumentResponse>(url, {
      params: httpParams,
    });
  }

  /**
   * Get documents from Invoicing API using current tenant from URL
   */
  getInvoicingDocumentsCurrentTenant(
    identityDocument: string,
  ): Observable<InvoicingDocumentResponse> {
    const tenantId = this.tenantUrlUtil.getTenantId();
    if (!tenantId) {
      throw new Error("No tenant ID found in current route");
    }

    return this.getInvoicingDocuments({ tenantId, identityDocument });
  }

  /**
   * Get voucher PDF URL
   * @param tenantId Tenant ID (e.g., 'HPSA')
   * @param params Query parameters for the voucher
   * @returns Observable with the voucher PDF response
   */

  getVoucherPDF(
    tenantId: string,
    params: {
      documentTypeId: string;
      ruc: string;
      serieFolio: string;
    },
  ): Observable<VoucherPDFResponse> {
    const url = `${this.TRANSACTION_API_BASE}/${tenantId}/invoices/voucher`;

    const httpParams = new HttpParams()
      .set("document-type-id", params.documentTypeId)
      .set("ruc", params.ruc)
      .set("serie-folio", params.serieFolio)
      .set("type", "PDF");

    return this.http.get<VoucherPDFResponse>(url, {
      params: httpParams,
    });
  }

  getVoucherPDFCurrentTenant(params: {
    documentTypeId: string;
    ruc: string;
    serieFolio: string;
  }): Observable<any> {
    const tenantId = this.tenantUrlUtil.getTenantId();
    if (!tenantId) {
      throw new Error("No tenant ID found in current route");
    }

    return this.getVoucherPDF(tenantId, params);
  }

  resendEmail(invoice: Invoice, email: string): Observable<any> {
    const tenantId = this.tenantUrlUtil.getTenantId()!;

    const tenant = environment.tenants.find(
      (t) => t.tenantId === tenantId.toUpperCase(),
    );
    if (!tenant || !tenant.ruc) {
      throw new Error(`RUC not found for tenant ${tenantId}`);
    }

    const payload: ResendEmailPayload = {
      tenant: tenantId.toLowerCase(),
      email,
      ruc: tenant.ruc,
      serieFolio: invoice.folio,
      orderId: invoice.orderId || "",
    };

    const url = `${
      this.TRANSACTION_API_BASE
    }/${tenantId.toLowerCase()}/invoices/sendmail`;

    return this.http.post<any>(url, payload, {});
  }

  /**
   * Get vouchers by order ID and tenant ID
   * @param tenantId Tenant ID
   * @param orderId Order ID
   * @returns Observable with array of vouchers
   */
  getVouchersByOrder(tenantId: string, orderId: string): Observable<Voucher[]> {
    const url = `${this.INVOICING_API_BASE}/${tenantId}/documents/${orderId}`;

    return this.http.get<Voucher[]>(url).pipe(
      catchError((err) => {
        console.error("Error in find voucher by orderId and tenantId: ", err);
        return throwError(
          () => new Error("Error in find voucher by orderId and tenantId"),
        );
      }),
    );
  }
}
